import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, ArrowRight, Star } from "lucide-react";
import { api } from "@/lib/api";

const TYPE_LABEL = { brand: "Brand", outfitter: "Outfitter", guide: "Guide" };

/** A subtle "Featured partner" nudge for gear/hunt pages. */
export function PartnerNudge({ state, preferType, label = "Featured partner", className = "" }) {
  const [partner, setPartner] = useState(null);

  useEffect(() => {
    let active = true;
    // State-scoped (hunt pages): suggest partners serving that state, prefer the requested type.
    // Type-scoped (gear pages): pull that partner type directly (directory sorts featured first).
    const req = state
      ? api.partnerSuggest({ state, limit: 6 })
      : preferType
        ? api.partnerDirectory({ type: preferType, limit: 6 })
        : api.partnerFeatured(6);
    req.then((d) => {
      if (!active) return;
      const list = d.partners || [];
      const pick = (preferType && list.find((p) => p.partner_type === preferType)) || list[0] || null;
      setPartner(pick);
    }).catch(() => {});
    return () => { active = false; };
  }, [state, preferType]);

  if (!partner) return null;
  const p = partner;

  return (
    <Link to={p.public_path} className={`group flex items-center gap-4 bg-forest text-bone rounded-xl p-4 hover:bg-forest-2 transition-colors ${className}`} data-testid="partner-nudge">
      <div className="w-14 h-14 rounded-lg bg-bone/10 overflow-hidden grid place-items-center shrink-0">
        {p.logo ? <img src={p.logo} alt={p.name} className="w-full h-full object-cover" /> : <span className="font-serif text-xl">{(p.name || "H")[0]}</span>}
      </div>
      <div className="min-w-0 flex-1">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-blaze"><Star size={9} className="fill-blaze" /> {label}</span>
        <div className="flex items-center gap-1.5 mt-0.5">
          <p className="font-serif text-lg truncate">{p.name}</p>
          {(p.verification_status || "").startsWith("verified") && <BadgeCheck size={14} className="text-blaze shrink-0" />}
        </div>
        {p.tagline && <p className="text-xs text-bone/60 truncate">{p.tagline}</p>}
      </div>
      <span className="text-[11px] font-bold uppercase tracking-wide bg-bone/10 px-2 py-1 rounded shrink-0 hidden sm:block">{TYPE_LABEL[p.partner_type]}</span>
      <ArrowRight size={16} className="text-bone/50 group-hover:text-blaze group-hover:translate-x-1 transition-all shrink-0" />
    </Link>
  );
}
